import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import YouTubeIcon from "@mui/icons-material/YouTube";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  SvgIcon,
  Tooltip,
  Typography,
} from "@mui/material";
import LoadingButton from "../../../components/ui/LoadingButton";
import IconButton from "../../../components/ui/IconButton";
import StatusPill, { StatusPillLevel } from "../../../components/ui/StatusPill";
import { useTheme } from "@mui/material/styles";
import dayjs from "dayjs";
import { useMemo } from "react";
import { useIntl } from "../../../contexts/IntlContext";
import { YOUTUBE_STATUS_PILL_LEVEL_BY_TOKEN_STATUS } from "../../../constants/youtubeTokenStatus";
import { YoutubeOwnerConnectionStatus } from "../../../services/youtubeOwnerConnection";
import { getCardSurfaceSx } from "../../../themes/darkSurfaceColors";

function WhatsAppIcon(props: { fontSize?: "small" | "inherit" | "large" | "medium" }) {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24" sx={{ color: "#25D366" }}>
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.435 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </SvgIcon>
  );
}

type Props = {
  status: YoutubeOwnerConnectionStatus | null;
  loading: boolean;
  blockingReconnect: boolean;
  supportWhatsAppHref: string;
  onConnect: () => void;
  onDisconnect: () => void;
};

export default function OwnerYoutubeConnection({
  status,
  loading,
  blockingReconnect,
  supportWhatsAppHref,
  onConnect,
  onDisconnect,
}: Props) {
  const intl = useIntl();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const cardSx = useMemo(() => getCardSurfaceSx(theme, isDark), [theme, isDark]);

  const isConnected = !!status?.connected;
  const connectLabelId = status?.requiresReconnect ? "youtubeConnection.reconnect" : "youtubeConnection.connect";
  const renewWhenConnectedLabelId = status?.requiresReconnect
    ? "youtubeConnection.reconnect"
    : "youtubeConnection.refreshConnection";

  const isRenewalError = status?.tokenStatus === "RENEWAL_ERROR";
  const statusPillLevel: StatusPillLevel =
    (status?.tokenStatus
      ? YOUTUBE_STATUS_PILL_LEVEL_BY_TOKEN_STATUS[status.tokenStatus]
      : undefined) ?? (isConnected ? (status?.requiresReconnect ? "warning" : "success") : "neutral");
  const statusLabelId = isRenewalError
    ? "youtubeConnection.renewalError"
    : isConnected
      ? status?.requiresReconnect
        ? "youtubeConnection.requiresReconnect"
        : "youtubeConnection.connected"
      : "youtubeConnection.disconnected";
  const lastSyncedAtLabel = status?.lastSyncedAt ? dayjs(status.lastSyncedAt).format("DD/MM/YYYY [às] HH:mm") : null;

  const prerequisitesTooltip = useMemo(
    () => (
      <Box sx={{ maxWidth: 360, py: 0.5 }}>
        <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
          {intl.formatMessage({ id: "youtubeConnection.prerequisitesInfo" })}
        </Typography>
      </Box>
    ),
    [intl]
  );

  return (
    <Card sx={cardSx}>
      <CardContent>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} justifyContent="space-between" alignItems={{ md: "center" }}>
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexWrap: "nowrap" }}>
              <Typography variant="h6" component="span">
                {intl.formatMessage({ id: "youtubeConnection.title" })}
              </Typography>
              <Tooltip title={prerequisitesTooltip} arrow placement="top-start" enterTouchDelay={0}>
                <span>
                  <IconButton
                    aria-label={intl.formatMessage({ id: "youtubeConnection.prerequisitesAria" })}
                    sx={{ color: "text.secondary" }}
                  >
                    <InfoOutlinedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {intl.formatMessage({ id: "youtubeConnection.subtitle" })}
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" alignItems="center">
              <StatusPill size="small" level={statusPillLevel} label={intl.formatMessage({ id: statusLabelId })} />
              {status?.channelName ? (
                <Chip
                  size="small"
                  label={status.channelName}
                  avatar={status.channelImageUrl ? <Avatar src={status.channelImageUrl} /> : undefined}
                />
              ) : null}
            </Stack>
            {lastSyncedAtLabel ? (
              <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.5 }}>
                {intl.formatMessage({ id: "youtubeConnection.lastSyncedAt" }, { date: lastSyncedAtLabel })}
              </Typography>
            ) : null}
          </Box>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} flexWrap="wrap" useFlexGap>
            <Button
              variant="outlined"
              color="success"
              component="a"
              size="small"
              href={supportWhatsAppHref}
              target="_blank"
              rel="noopener noreferrer"
              startIcon={<WhatsAppIcon fontSize="small" />}
            >
              {intl.formatMessage({ id: "youtubeConnection.support" })}
            </Button>
            {!isConnected ? (
              <LoadingButton
                variant="contained"
                color="warning"
                size="small"
                onClick={() => onConnect()}
                disabled={loading}
                startIcon={<YouTubeIcon />}
                fullWidth={false}
              >
                {intl.formatMessage({ id: connectLabelId })}
              </LoadingButton>
            ) : (
              <>
                <LoadingButton
                  variant="contained"
                  color="warning"
                  size="small"
                  onClick={() => onConnect()}
                  disabled={loading}
                  startIcon={<YouTubeIcon />}
                  fullWidth={false}
                >
                  {intl.formatMessage({ id: renewWhenConnectedLabelId })}
                </LoadingButton>
                <LoadingButton
                  variant="outlined"
                  color="error"
                  size="small"
                  onClick={() => onDisconnect()}
                  disabled={loading}
                  startIcon={<LinkOffIcon />}
                  fullWidth={false}
                >
                  {intl.formatMessage({ id: "youtubeConnection.disconnect" })}
                </LoadingButton>
              </>
            )}
          </Stack>
        </Stack>
        {blockingReconnect ? (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {intl.formatMessage({ id: "youtubeConnection.blockingWarning" })}
          </Alert>
        ) : null}
      </CardContent>
    </Card>
  );
}
